'use client';
/** @jsxImportSource @emotion/react */

import React from 'react';

import type { DialogProps as MuiDialogProps } from '@mui/material/Dialog';

import MuiDialog from '@mui/material/Dialog';

type ExcludedMuiProps =
  // A11y : Escape is a common way for people to close a modal
  | 'disableEscapeKeyDown'

  // Animation : future theme tokens
  | 'BackdropComponent'
  | 'BackdropProps'
  /**
   * Removed the `component` prop from the `Dialog` component, as it should not be modified since its behaviour
   * and layout are tightly controlled by MUI, including accessibility features like focus management and
   * modal handling.
   * And the default implementation in MUI is also broken, and altering it could lead to issues.
   *
   * https://serko.atlassian.net/browse/ZDS-1032
   */
  | 'component'
  | 'TransitionComponent'
  | 'transitionDuration'
  | 'TransitionProps';

interface DialogProps extends Omit<MuiDialogProps, ExcludedMuiProps> {
  /**
   * Refers to the container element where the dialog should be rendered.
   */
  containerRef?: React.RefObject<HTMLElement | null>;
}

export type { DialogProps };
export const Dialog = React.forwardRef<HTMLDivElement, DialogProps>(
  ({ children, containerRef, slotProps, sx, ...rest }, ref) => (
    <MuiDialog
      {...rest}
      ref={ref}
      /*
       * Using `containerRef` avoids potential breaking changes and unintended side effects
       * related to `sx` and `slotProps` adjustments. It ensures precise control over dialog positioning.
       *
       * Note: Switching to or adding a `container` prop could impact existing implementations.
       */
      {...(containerRef && { container: containerRef.current })}
      slotProps={
        containerRef
          ? { backdrop: { style: { position: 'inherit' } }, ...slotProps }
          : slotProps
      }
      /**
       * Setting the `closeAfterTransition` prop of the Modal to false (it's enabled by default in Dialog), the `aria-hidden` error disappears because the Dialog executes onClose immediately before transition, removing the `aria-hidden` attribute without delay.
       */
      closeAfterTransition={false}
      sx={containerRef ? { position: 'absolute', ...sx } : sx}
    >
      {children}
    </MuiDialog>
  )
);

Dialog.displayName = 'Dialog';
